import React, { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import CurrencyComponent from '../../../Elements/currency'
import { selectCompanyData } from '../../../../redux/selector/system'
import { getCurrencyLocaleByCountryCode, getFormattedCurrency } from '../../../../helper'
import { Guest, Money, SearchMode } from '../../types'
import { checkEligibility, toMajor, toMinor } from '../../helper'
import {
	SelectGuestButton, SelectGuestChevron, SelectGuestInfo, SelectGuestList, SelectGuestName, SelectGuestNameRow, SelectGuestRoomBadge, SelectGuestStatus, SelectGuestStatusIcon, SelectLimitDot, SelectLimitLabel, SelectLimitRow, SelectLimitValue, SelectSummaryIcon, SelectSummaryRow,
} from './index.styled'

const Currency = CurrencyComponent as unknown as React.ComponentType<{ children: number }>

type Props = {
	guests: Guest[]
	searchMode: SearchMode
	roomNumber: string
	guestName: string
	total: number
	roomLimit: Money | null
	onSelect: (guest: Guest) => void
}

const RoomChargeSelectStep = React.memo(({
	guests, searchMode, roomNumber, guestName, total, roomLimit, onSelect,
}: Props) => {
	const { t } = useTranslation()
	const countryCode = useSelector(selectCompanyData('countryCode'))
	const currencyCode = useSelector(selectCompanyData('currency'))

	const formatMoney = useCallback(
		(value: number) => getFormattedCurrency(value, getCurrencyLocaleByCountryCode(countryCode), currencyCode),
		[countryCode, currencyCode],
	)

	const handleSelect = useCallback((guest: Guest) => () => onSelect(guest), [onSelect])

	const withinLimit = !roomLimit || toMinor(total) <= roomLimit.amount
	const limitLayout = withinLimit ? '' : 'exceeded'

	const summaryText = searchMode === 'name'
		? t(guests.length === 1 ? 'RoomCharge.select.oneByName' : 'RoomCharge.select.manyByName', { count: guests.length, name: guestName.trim() })
		: t(guests.length === 1 ? 'RoomCharge.select.oneByRoom' : 'RoomCharge.select.manyByRoom', { count: guests.length, room: roomNumber })

	return (
		<>
			<SelectSummaryRow>
				<SelectSummaryIcon icon="users" />
				<span>{summaryText}</span>
			</SelectSummaryRow>

			{roomLimit &&
				<SelectLimitRow className={limitLayout}>
					<SelectLimitLabel>
						<SelectLimitDot className={limitLayout} />
						{t('RoomCharge.select.availableLimit', { room: roomNumber })}
					</SelectLimitLabel>
					<SelectLimitValue><Currency>{toMajor(roomLimit)}</Currency></SelectLimitValue>
				</SelectLimitRow>}

			<SelectGuestList>
				{guests.map(guest => {
					const eligibility = checkEligibility(guest, total)
					const guestLayout = eligibility.ok ? '' : 'blocked'
					return (
						<SelectGuestButton key={guest.guestId}
															 type="button"
															 disabled={!eligibility.ok}
															 onClick={handleSelect(guest)}>
							<SelectGuestInfo>
								<SelectGuestNameRow>
									<SelectGuestName className={guestLayout}>{guest.fullName}</SelectGuestName>
									<SelectGuestRoomBadge>
										{t('RoomCharge.select.roomShort', { room: guest.roomNumber })}
									</SelectGuestRoomBadge>
								</SelectGuestNameRow>
								<SelectGuestStatus className={guestLayout}>
									<SelectGuestStatusIcon icon={eligibility.ok ? 'shield-check' : 'alert-triangle'} />
									{eligibility.ok ?
										t('RoomCharge.select.eligible') :
										eligibility.code === 'credit_limit_exceeded' && eligibility.remaining ?
											t('RoomCharge.errors.credit_limit_exceeded_amount', { amount: formatMoney(toMajor(eligibility.remaining)) }) :
											t(`RoomCharge.errors.${eligibility.code}`)}
								</SelectGuestStatus>
							</SelectGuestInfo>
							<SelectGuestChevron icon="chevron-right" />
						</SelectGuestButton>
					)
				})}
			</SelectGuestList>
		</>
	)
})

RoomChargeSelectStep.displayName = 'RoomChargeSelectStep'
export default RoomChargeSelectStep
